import React from 'react';
import { motion } from 'framer-motion';
import { FiZap, FiSmartphone, FiLayers, FiCode, FiImage, FiCpu } from 'react-icons/fi';
import './LandingPageDemo.css';

// Landing Page Demo - foco em UI/UX com animações leves e interações
// - Hero com mouse parallax (via CSS vars)
// - Cards de serviços/recursos com hover
// - Galeria mini (itens de portfólio)
// - Badges de tecnologias

const LandingPageDemo = () => {
  const heroRef = React.useRef(null);
  const ctaRef = React.useRef(null);

  React.useEffect(() => {
    const el = heroRef.current;
    if (!el) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100; // 0-100
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--mx', `${x}%`);
      el.style.setProperty('--my', `${y}%`);
    };

    const onLeave = () => {
      el.style.setProperty('--mx', `50%`);
      el.style.setProperty('--my', `0%`);
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  React.useEffect(() => {
    const btn = ctaRef.current;
    if (!btn) return;

    const onDown = (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      btn.style.setProperty('--rx', `${x}px`);
      btn.style.setProperty('--ry', `${y}px`);
      btn.classList.add('is-pressing');
    };
    const onUp = () => btn.classList.remove('is-pressing');

    btn.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    return () => {
      btn.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const handleWhatsAppClick = () => {
    // Usando o formato oficial do WhatsApp
    const phone = '5575998042767'; // sem + e sem espaços
    const msg = encodeURIComponent('Olá! Vi sua Landing Page e quero um site.');
    const url = `https://wa.me/${phone}?text=${msg}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="landing-demo">
      <div className="ld-header">
        <h3>Landing Page — Demo Interativo</h3>
      </div>
      <p className="ld-subtitle">Layout moderno com hero animado, cartões de serviço, recursos e vitrine.</p>

      {/* Hero */}
      <div ref={heroRef} className="hero-card">
        <h4 className="hero-title">Crie presença digital com impacto</h4>
        <p className="hero-text">Landing pages focadas em conversão, performance e SEO — com design responsivo e animações sutis.</p>
        <div className="hero-actions">
          <button
            ref={ctaRef}
            className="btn primary ripple-btn"
            onClick={handleWhatsAppClick}
            title="Falar no WhatsApp"
          >
            Quero um site
          </button>
        </div>
      </div>

      {/* Serviços */}
      <div className="panel-title">Serviços</div>
      <div className="grid" style={{ marginBottom: 16 }}>
        <motion.div whileHover={{ y: -3 }} className="service-card">
          <div className="row"><FiZap /> <strong className="service-title">Performance</strong></div>
          <p className="service-desc">Carregamento rápido, Lighthouse alto e otimização de assets.</p>
        </motion.div>
        <motion.div whileHover={{ y: -3 }} className="service-card">
          <div className="row"><FiSmartphone /> <strong className="service-title">Responsividade</strong></div>
          <p className="service-desc">Experiência fluida do mobile ao desktop.</p>
        </motion.div>
        <motion.div whileHover={{ y: -3 }} className="service-card">
          <div className="row"><FiLayers /> <strong className="service-title">Design System</strong></div>
          <p className="service-desc">Componentes consistentes, escaláveis e acessíveis.</p>
        </motion.div>
      </div>

      {/* Recursos */}
      <div className="panel-title">Recursos</div>
      <div className="grid" style={{ marginBottom: 16 }}>
        <motion.div whileHover={{ y: -3 }} className="feature-card">
          <h4>Seções-chave</h4>
          <p>Hero, features, prova social, CTA e FAQ.</p>
        </motion.div>
        <motion.div whileHover={{ y: -3 }} className="feature-card">
          <h4>Interações</h4>
          <p>Microinterações com hover, ripple e parallax leve.</p>
        </motion.div>
        <motion.div whileHover={{ y: -3 }} className="feature-card">
          <h4>SEO/Analytics</h4>
          <p>Metatags, estrutura semântica e mensuração.</p>
        </motion.div>
      </div>

      {/* Portfólio */}
      <div className="panel-title">Vitrine</div>
      <div className="grid" style={{ marginBottom: 16 }}>
        <div className="portfolio-item">
          <img 
            src="https://images.unsplash.com/photo-1559028012-481c04fa702d?q=80&w=1200&auto=format&fit=crop" 
            alt="Projeto 1" 
            loading="lazy" 
            referrerPolicy="no-referrer"
          />
          <div className="row"><FiImage className="sep"/> <span>Projeto de e-commerce</span></div>
        </div>
        <div className="portfolio-item">
          <img 
            src="https://images.unsplash.com/photo-1522199670076-2852f80289c7?q=80&w=1200&auto=format&fit=crop" 
            alt="Projeto 2" 
            loading="lazy" 
            referrerPolicy="no-referrer"
          />
          <div className="row"><FiImage className="sep"/> <span>Landing conversão</span></div>
        </div>
        <div className="portfolio-item">
          <img 
            src="https://images.unsplash.com/photo-1547658719-da2b51169166?q=80&w=1200&auto=format&fit=crop" 
            alt="Projeto 3" 
            loading="lazy" 
            referrerPolicy="no-referrer"
          />
          <div className="row"><FiImage className="sep"/> <span>Dashboard marketing</span></div>
        </div>
      </div>

      {/* Techs */}
      <div className="panel-title">Tecnologias</div>
      <div className="techs">
        <div className="tech-item"><FiCode /> React</div>
        <div className="tech-item"><FiCpu /> Framer Motion</div>
        <div className="tech-item"><FiLayers /> CSS3</div>
      </div>
    </div>
  );
};

export default LandingPageDemo;